import React, { useState } from "react";
import {
  Megaphone,
  FileText,
  Calendar,
  Share2,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Image,
  Video,
  Globe,
  Users,
  TrendingUp,
  MessageSquare,
  Send,
  Bell,
  Target,
  BarChart3,
  Heart,
  Zap as Lightning,
  Volume2 as Volume2Icon,
  Printer as PrinterIcon,
  Headphones,
  Speaker,
  Radio,
  Tv,
  Gamepad2,
  Joystick,
  Mouse,
  Keyboard,
  MousePointer,
  Touchpad,
  Webcam,
  Watch,
  Clock as ClockIcon,
  Timer,
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarCheck,
  CalendarX,
  CalendarPlus,
  CalendarMinus,
  CalendarRange,
  CalendarSearch,
  CalendarHeart,
  CalendarClock,
  CalendarX2,
} from "lucide-react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import Modal from "../components/ui/Modal";
import Badge from "../components/ui/Badge";
import { format } from "date-fns";

interface Content {
  id: string;
  title: string;
  type: "press-release" | "newsletter" | "announcement" | "social-media";
  status: "draft" | "review" | "approved" | "published";
  author: string;
  createdDate: string;
  publishedDate?: string;
  category: string;
  tags: string[];
}

interface Campaign {
  id: string;
  name: string;
  type: "awareness" | "recruitment" | "event" | "crisis";
  status: "planning" | "active" | "completed" | "paused";
  startDate: string;
  endDate: string;
  budget: number;
  reach: number;
  engagement: number;
}

export default function Media() {
  const [activeTab, setActiveTab] = useState<
    | "content"
    | "campaigns"
    | "monitoring"
    | "social"
    | "analytics"
    | "announcements"
  >("content");
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const contentItems: Content[] = [
    {
      id: "1",
      title: "New Pilot Training Program Launch",
      type: "press-release",
      status: "published",
      author: "Media Team",
      createdDate: "2024-01-15",
      publishedDate: "2024-01-16",
      category: "Training",
      tags: ["training", "pilots", "education"],
    },
    {
      id: "2",
      title: "Monthly Newsletter - January 2024",
      type: "newsletter",
      status: "approved",
      author: "Editorial Team",
      createdDate: "2024-01-20",
      category: "Newsletter",
      tags: ["newsletter", "updates", "members"],
    },
    {
      id: "3",
      title: "Annual General Meeting Announcement",
      type: "announcement",
      status: "review",
      author: "Board Secretary",
      createdDate: "2024-01-22",
      category: "Events",
      tags: ["meeting", "announcement", "members"],
    },
  ];

  const campaigns: Campaign[] = [
    {
      id: "1",
      name: "Pilot Recruitment Drive 2024",
      type: "recruitment",
      status: "active",
      startDate: "2024-01-01",
      endDate: "2024-03-31",
      budget: 50000,
      reach: 15000,
      engagement: 2500,
    },
    {
      id: "2",
      name: "Safety Awareness Campaign",
      type: "awareness",
      status: "completed",
      startDate: "2023-12-01",
      endDate: "2023-12-31",
      budget: 30000,
      reach: 25000,
      engagement: 5000,
    },
  ];

  const mediaMonitoring = [
    {
      id: "1",
      source: "Aviation Weekly",
      title: "Egyptian Pilots Union Announces New Training Initiative",
      sentiment: "positive",
      date: "2024-01-18",
      reach: 5000,
    },
    {
      id: "2",
      source: "Cairo News",
      title: "Pilot Union Addresses Industry Challenges",
      sentiment: "neutral",
      date: "2024-01-16",
      reach: 12000,
    },
  ];

  // Sample data for new features
  const socialMediaAccounts = [
    {
      platform: "Facebook",
      followers: 2500,
      engagement: 4.2,
      posts: 45,
      status: "active",
    },
    {
      platform: "Twitter",
      followers: 1800,
      engagement: 3.8,
      posts: 32,
      status: "active",
    },
    {
      platform: "LinkedIn",
      followers: 3200,
      engagement: 5.1,
      posts: 28,
      status: "active",
    },
    {
      platform: "Instagram",
      followers: 1200,
      engagement: 6.3,
      posts: 38,
      status: "active",
    },
  ];

  const announcements = [
    {
      id: 1,
      title: "Annual General Meeting 2024",
      type: "meeting",
      priority: "high",
      date: "2024-02-15",
      status: "published",
    },
    {
      id: 2,
      title: "New Membership Benefits",
      type: "benefits",
      priority: "medium",
      date: "2024-02-10",
      status: "draft",
    },
    {
      id: 3,
      title: "Safety Training Update",
      type: "training",
      priority: "high",
      date: "2024-02-05",
      status: "published",
    },
  ];

  const analytics = {
    totalReach: 125000,
    engagementRate: 4.8,
    contentPerformance: [
      { type: "Press Release", views: 15000, engagement: 850, conversion: 12 },
      { type: "Newsletter", views: 8500, engagement: 420, conversion: 8 },
      { type: "Social Media", views: 25000, engagement: 1200, conversion: 15 },
    ],
    topContent: [
      {
        title: "New Pilot Training Program",
        views: 8500,
        engagement: 420,
        date: "2024-01-15",
      },
      {
        title: "Safety Guidelines Update",
        views: 7200,
        engagement: 380,
        date: "2024-01-12",
      },
      {
        title: "Member Success Stories",
        views: 6800,
        engagement: 350,
        date: "2024-01-10",
      },
    ],
  };

  const handleCreateCampaign = (campaignData: any) => {
    // Simulate campaign creation
    // Creating campaign
    setShowCampaignModal(false);
  };

  const handleCreateAnnouncement = (announcementData: any) => {
    // Simulate announcement creation
    // Creating announcement
    setShowAnnouncementModal(false);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "draft":
        return <Badge variant="default">Draft</Badge>;
      case "review":
        return <Badge variant="warning">Under Review</Badge>;
      case "approved":
        return <Badge variant="info">Approved</Badge>;
      case "published":
        return <Badge variant="success">Published</Badge>;
      case "planning":
        return <Badge variant="default">Planning</Badge>;
      case "active":
        return <Badge variant="success">Active</Badge>;
      case "completed":
        return <Badge variant="info">Completed</Badge>;
      case "paused":
        return <Badge variant="warning">Paused</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case "press-release":
        return <Badge variant="info">Press Release</Badge>;
      case "newsletter":
        return <Badge variant="info">Newsletter</Badge>;
      case "announcement":
        return <Badge variant="warning">Announcement</Badge>;
      case "social-media":
        return <Badge variant="warning">Social Media</Badge>;
      case "recruitment":
        return <Badge variant="success">Recruitment</Badge>;
      case "awareness":
        return <Badge variant="info">Awareness</Badge>;
      case "event":
        return <Badge variant="warning">Event</Badge>;
      case "crisis":
        return <Badge variant="error">Crisis</Badge>;
      default:
        return <Badge variant="default">{type}</Badge>;
    }
  };

  const getSentimentBadge = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return <Badge variant="success">Positive</Badge>;
      case "negative":
        return <Badge variant="error">Negative</Badge>;
      case "neutral":
        return <Badge variant="default">Neutral</Badge>;
      default:
        return <Badge variant="default">{sentiment}</Badge>;
    }
  };

  const contentColumns = [
    {
      key: "title",
      header: "Content",
      render: (item: Content) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{item.title}</div>
          <div className="text-sm text-gray-500">{item.category}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (item: Content) => getTypeBadge(item.type),
    },
    {
      key: "author",
      header: "Author",
      render: (item: Content) => item.author,
    },
    {
      key: "status",
      header: "Status",
      render: (item: Content) => getStatusBadge(item.status),
    },
    {
      key: "createdDate",
      header: "Created",
      render: (item: Content) =>
        format(new Date(item.createdDate), "MMM dd, yyyy"),
    },
    {
      key: "publishedDate",
      header: "Published",
      render: (item: Content) =>
        item.publishedDate
          ? format(new Date(item.publishedDate), "MMM dd, yyyy")
          : "-",
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: Content) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedContent(item);
              setShowContentModal(true);
            }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedContent(item);
              setShowContentModal(true);
            }}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const campaignColumns = [
    {
      key: "name",
      header: "Campaign",
      render: (item: Campaign) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{item.name}</div>
          <div className="text-sm text-gray-500">#{item.id}</div>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (item: Campaign) => getTypeBadge(item.type),
    },
    {
      key: "status",
      header: "Status",
      render: (item: Campaign) => getStatusBadge(item.status),
    },
    {
      key: "startDate",
      header: "Start Date",
      render: (item: Campaign) =>
        format(new Date(item.startDate), "MMM dd, yyyy"),
    },
    {
      key: "endDate",
      header: "End Date",
      render: (item: Campaign) =>
        format(new Date(item.endDate), "MMM dd, yyyy"),
    },
    {
      key: "budget",
      header: "Budget",
      render: (item: Campaign) => `EGP ${item.budget.toLocaleString()}`,
    },
    {
      key: "reach",
      header: "Reach",
      render: (item: Campaign) => item.reach.toLocaleString(),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: Campaign) => (
        <div className="flex space-x-2">
          <Button variant="ghost" size="sm">
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm">
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Media & Communication
          </h1>
          <p className="text-gray-600">
            Content planning, social media integration, and announcements
          </p>
        </div>
        <div className="flex gap-2">
          {activeTab === "content" && (
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Content
            </Button>
          )}
          {activeTab === "campaigns" && (
            <Button onClick={() => setShowCampaignModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          )}
          {activeTab === "announcements" && (
            <Button onClick={() => setShowAnnouncementModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Announcement
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Content</p>
              <p className="text-2xl font-bold text-gray-900">
                {contentItems.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <Megaphone className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Published</p>
              <p className="text-2xl font-bold text-gray-900">
                {contentItems.filter((c) => c.status === "published").length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Active Campaigns
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {campaigns.filter((c) => c.status === "active").length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Globe className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                Media Mentions
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {mediaMonitoring.length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "content", name: "Content", icon: FileText },
            { id: "campaigns", name: "Campaigns", icon: Megaphone },
            { id: "monitoring", name: "Media Monitoring", icon: Eye },
            { id: "social", name: "Social Media", icon: Share2 },
            { id: "analytics", name: "Analytics", icon: BarChart3 },
            { id: "announcements", name: "Announcements", icon: Bell },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "content" && (
        <div className="space-y-6">
          {/* Search and Filters */}
          <Card>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search content..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Types</option>
                  <option>Press Release</option>
                  <option>Newsletter</option>
                  <option>Announcement</option>
                </select>
                <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option>All Status</option>
                  <option>Draft</option>
                  <option>Under Review</option>
                  <option>Approved</option>
                  <option>Published</option>
                </select>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </div>
            </div>
          </Card>

          {/* Content Table */}
          <Card padding="none">
            <Table
              data={contentItems}
              columns={contentColumns}
              onRowClick={(item) => {
                setSelectedContent(item);
                setShowContentModal(true);
              }}
              emptyMessage="No content found"
            />
          </Card>
        </div>
      )}

      {activeTab === "campaigns" && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Marketing Campaigns
            </h3>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          </div>
          <Card padding="none">
            <Table
              data={campaigns}
              columns={campaignColumns}
              emptyMessage="No campaigns found"
            />
          </Card>
        </div>
      )}

      {activeTab === "monitoring" && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Media Monitoring Dashboard
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <h4 className="font-semibold text-gray-900 mb-4">
                Recent Mentions
              </h4>
              <div className="space-y-4">
                {mediaMonitoring.map((mention) => (
                  <div key={mention.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="text-sm font-medium text-gray-900">
                        {mention.title}
                      </h5>
                      {getSentimentBadge(mention.sentiment)}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Source: {mention.source}
                    </p>
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <span>
                        {format(new Date(mention.date), "MMM dd, yyyy")}
                      </span>
                      <span>Reach: {mention.reach.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <h4 className="font-semibold text-gray-900 mb-4">
                Sentiment Analysis
              </h4>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Positive</span>
                  <span className="text-sm font-medium text-green-600">
                    60%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: "60%" }}
                  ></div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Neutral</span>
                  <span className="text-sm font-medium text-gray-600">30%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gray-500 h-2 rounded-full"
                    style={{ width: "30%" }}
                  ></div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Negative</span>
                  <span className="text-sm font-medium text-red-600">10%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full"
                    style={{ width: "10%" }}
                  ></div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "social" && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Social Media Management
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {socialMediaAccounts.map((account) => (
              <Card key={account.platform}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <Globe className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {account.platform}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {account.followers.toLocaleString()} followers
                    </p>
                  </div>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Engagement</span>
                    <span className="font-medium">{account.engagement}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Posts</span>
                    <span className="font-medium">{account.posts}</span>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  <Share2 className="h-4 w-4 mr-2" />
                  Post Update
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === "analytics" && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Media Analytics Dashboard
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <div className="text-center">
                <div className="p-3 bg-blue-100 rounded-lg mx-auto w-fit mb-3">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">
                  {analytics.totalReach.toLocaleString()}
                </h4>
                <p className="text-sm text-gray-600">Total Reach</p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="p-3 bg-green-100 rounded-lg mx-auto w-fit mb-3">
                  <Heart className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">
                  {analytics.engagementRate}%
                </h4>
                <p className="text-sm text-gray-600">Engagement Rate</p>
              </div>
            </Card>
            <Card>
              <div className="text-center">
                <div className="p-3 bg-purple-100 rounded-lg mx-auto w-fit mb-3">
                  <Target className="h-6 w-6 text-purple-600" />
                </div>
                <h4 className="text-2xl font-bold text-gray-900">12</h4>
                <p className="text-sm text-gray-600">Active Campaigns</p>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <h4 className="font-semibold text-gray-900 mb-4">
                Content Performance
              </h4>
              <div className="space-y-4">
                {analytics.contentPerformance.map((content) => (
                  <div
                    key={content.type}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {content.type}
                      </p>
                      <p className="text-xs text-gray-500">
                        {content.views.toLocaleString()} views
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {content.engagement} engagements
                      </p>
                      <p className="text-xs text-gray-500">
                        {content.conversion}% conversion
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <h4 className="font-semibold text-gray-900 mb-4">
                Top Performing Content
              </h4>
              <div className="space-y-3">
                {analytics.topContent.map((content, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {content.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(content.date), "MMM dd, yyyy")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {content.views.toLocaleString()} views
                      </p>
                      <p className="text-xs text-gray-500">
                        {content.engagement} engagements
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "announcements" && (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Member Announcements
          </h3>
          <div className="grid grid-cols-1 gap-4">
            {announcements.map((announcement) => (
              <Card key={announcement.id}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {announcement.title}
                    </h4>
                    <div className="flex items-center mt-2 space-x-4">
                      <Badge
                        variant={
                          announcement.priority === "high" ? "error" : "warning"
                        }
                      >
                        {announcement.priority} priority
                      </Badge>
                      <Badge
                        variant={
                          announcement.status === "published"
                            ? "success"
                            : "default"
                        }
                      >
                        {announcement.status}
                      </Badge>
                      <span className="text-sm text-gray-500">
                        {format(new Date(announcement.date), "MMM dd, yyyy")}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Content Details Modal */}
      {selectedContent && (
        <ContentDetailsModal
          content={selectedContent}
          isOpen={showContentModal}
          onClose={() => {
            setShowContentModal(false);
            setSelectedContent(null);
          }}
        />
      )}

      {/* Add Content Modal */}
      <AddContentModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {/* Campaign Modal */}
      <CampaignModal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        onCreate={handleCreateCampaign}
      />

      {/* Announcement Modal */}
      <AnnouncementModal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        onCreate={handleCreateAnnouncement}
      />
    </div>
  );
}

// Content Details Modal Component
function ContentDetailsModal({
  content,
  isOpen,
  onClose,
}: {
  content: Content;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Content Details" size="lg">
      <div className="space-y-6">
        {/* Content Header */}
        <div>
          <h3 className="text-xl font-bold text-gray-900">{content.title}</h3>
          <div className="flex items-center mt-2 space-x-4">
            <Badge
              variant={
                content.type === "press-release"
                  ? "info"
                  : content.type === "newsletter"
                  ? "default"
                  : "warning"
              }
            >
              {content.type}
            </Badge>
            <Badge
              variant={
                content.status === "published"
                  ? "success"
                  : content.status === "review"
                  ? "warning"
                  : "default"
              }
            >
              {content.status}
            </Badge>
          </div>
        </div>

        {/* Content Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Content Information</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Author
                </label>
                <p className="text-sm text-gray-900">{content.author}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <p className="text-sm text-gray-900">{content.category}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Created Date
                </label>
                <p className="text-sm text-gray-900">
                  {format(new Date(content.createdDate), "MMM dd, yyyy")}
                </p>
              </div>
              {content.publishedDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Published Date
                  </label>
                  <p className="text-sm text-gray-900">
                    {format(new Date(content.publishedDate), "MMM dd, yyyy")}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-gray-900">Tags</h4>
            <div className="flex flex-wrap gap-2">
              {content.tags.map((tag, index) => (
                <Badge key={index} variant="info">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button>
            <Edit className="h-4 w-4 mr-2" />
            Edit Content
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Add Content Modal Component
function AddContentModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    type: "press-release" as const,
    category: "",
    content: "",
    tags: [] as string[],
  });

  const [newTag, setNewTag] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    onClose();
  };

  const addTag = () => {
    if (newTag.trim()) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()],
      });
      setNewTag("");
    }
  };

  const removeTag = (index: number) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((_, i) => i !== index),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Content"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Content Title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Content Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as any })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="press-release">Press Release</option>
              <option value="newsletter">Newsletter</option>
              <option value="announcement">Announcement</option>
              <option value="social-media">Social Media</option>
            </select>
          </div>
          <Input
            label="Category"
            value={formData.category}
            onChange={(e) =>
              setFormData({ ...formData, category: e.target.value })
            }
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            value={formData.content}
            onChange={(e) =>
              setFormData({ ...formData, content: e.target.value })
            }
            rows={8}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tags
          </label>
          <div className="flex gap-2 mb-3">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Add tag"
              className="flex-1"
            />
            <Button type="button" onClick={addTag}>
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.tags.map((tag, index) => (
              <Badge
                key={index}
                variant="info"
                className="flex items-center gap-1"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(index)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Create Content</Button>
        </div>
      </form>
    </Modal>
  );
}

// Campaign Modal Component
function CampaignModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (campaign: any) => void;
}) {
  const [formData, setFormData] = useState({
    name: "",
    type: "awareness",
    startDate: "",
    endDate: "",
    budget: "",
    targetAudience: "",
    description: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      ...formData,
      budget: parseFloat(formData.budget),
      reach: 0,
      engagement: 0,
    });
    setFormData({
      name: "",
      type: "awareness",
      startDate: "",
      endDate: "",
      budget: "",
      targetAudience: "",
      description: "",
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Campaign"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Campaign Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="awareness">Awareness</option>
              <option value="recruitment">Recruitment</option>
              <option value="event">Event</option>
              <option value="crisis">Crisis</option>
            </select>
          </div>
          <Input
            label="Budget (EGP)"
            type="number"
            value={formData.budget}
            onChange={(e) =>
              setFormData({ ...formData, budget: e.target.value })
            }
            required
          />
          <Input
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={(e) =>
              setFormData({ ...formData, startDate: e.target.value })
            }
            required
          />
          <Input
            label="End Date"
            type="date"
            value={formData.endDate}
            onChange={(e) =>
              setFormData({ ...formData, endDate: e.target.value })
            }
            required
          />
        </div>
        <Input
          label="Target Audience"
          value={formData.targetAudience}
          onChange={(e) =>
            setFormData({ ...formData, targetAudience: e.target.value })
          }
          placeholder="e.g., Active pilots, New members, General public"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Campaign objectives and key messages..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Create Campaign</Button>
        </div>
      </form>
    </Modal>
  );
}

// Announcement Modal Component
function AnnouncementModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (announcement: any) => void;
}) {
  const [formData, setFormData] = useState({
    title: "",
    type: "meeting",
    priority: "medium",
    date: "",
    content: "",
    targetAudience: "all",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      ...formData,
      status: "draft",
    });
    setFormData({
      title: "",
      type: "meeting",
      priority: "medium",
      date: "",
      content: "",
      targetAudience: "all",
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Announcement"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Announcement Title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="meeting">Meeting</option>
              <option value="benefits">Benefits</option>
              <option value="training">Training</option>
              <option value="event">Event</option>
              <option value="policy">Policy</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <Input
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Audience
            </label>
            <select
              value={formData.targetAudience}
              onChange={(e) =>
                setFormData({ ...formData, targetAudience: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Members</option>
              <option value="active">Active Pilots</option>
              <option value="retired">Retired Members</option>
              <option value="board">Board Members</option>
              <option value="committees">Committee Members</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            value={formData.content}
            onChange={(e) =>
              setFormData({ ...formData, content: e.target.value })
            }
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Announcement content..."
            required
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">Create Announcement</Button>
        </div>
      </form>
    </Modal>
  );
}
